import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import { useCause } from "contexts/CauseContext";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { appConfig } from "../../config/appConfig";
import { handleTestAccountBypass } from "../../utils/auth/handleTestAccountBypass";
import { sendMagicLinkEmail } from "../../utils/auth/sendMagicLinkEmail";
import { useUserLogin } from "../authentication/hooks/useUserAuth";
import { getAccount, recordLogin } from "../../api/accountApi";
import * as Sentry from "@sentry/react";
import { OrganizationService } from "@/services/OrganizationService";

export type AfterFirebaseAuthBeforeLoginUserArgs = {
  userUid: string;
  email: string;
  isExistingUser: boolean;
};

export type UseAuthContainerOptions = {
  afterFirebaseAuthBeforeLoginUser?: (
    args: AfterFirebaseAuthBeforeLoginUserArgs
  ) => Promise<void>;
};

export type UseAuthContainerReturn = {
  error: string;
  emailForMagic: string;
  signUp: boolean;
  isLoading: boolean;
  successMessage: string;
  showEmailForm: boolean;
  showCodeInput: boolean;
  selectedOption: number | null;
  introCompleted: boolean;

  setEmailForMagic: (email: string) => void;
  handleOptionSelect: (option: number) => void;
  handleExplore: (expoTokenPayload?: unknown) => Promise<void>;
  handleIntroComplete: () => void;
  addOrganization: (organizationName: string) => Promise<string>;
  sendMagicLink: () => Promise<void>;
  handleEmailFormBack: () => void;
  handleCodeVerified: () => Promise<void>;
  handleBackToEmail: () => void;

  isOnline: boolean;
  loadingStates: any;
};

export function useStartup(
  options: UseAuthContainerOptions = {}
): UseAuthContainerReturn {
  const { afterFirebaseAuthBeforeLoginUser } = options;
  const [error, setError] = useState("");
  const [emailForMagic, setEmailForMagic] = useState("");
  const [signUp, setSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [introCompleted, setIntroCompleted] = useState(true); // TEMPORARILY DISABLED: Set to true to skip animated intro

  const { loadingStates, refreshData, setMoods } = useAppState();
  const { setUserId, setEmail, userId } = useAuth();
  const { setCauses } = useCause();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const networkStatus = useNetworkStatus() as {
    isOnline: boolean;
    isOffline: boolean;
    isSlowConnection: boolean;
    connectionType: string;
  };
  const isOnline = networkStatus.isOnline;
  const loginUser = useUserLogin({
    setUserId,
    setEmail,
    setMoods,
    setCauses,
    refreshData,
  });

  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === "magic-link-expired") {
      setError(
        "Magic link has expired or already been used. Please request a new one."
      );
    } else if (errorParam === "invalid-email") {
      setError("Invalid email address. Please check and try again.");
    }
  }, [searchParams]);

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam?.trim()) {
      setEmailForMagic(emailParam.trim());
    }
  }, [searchParams]);

  const handleOptionSelect = (option: number) => {
    if (option === 1 || option === 2) {
      setShowEmailForm(true);
      setSelectedOption(option);
    } else if (option === 3) {
      handleExplore();
    }
  };

  const handleExplore = async (expoTokenPayload?: unknown) => {
    try {
      const { signInAnonymously } = await import("firebase/auth");
      const { auth } = await import("../../config/firebase");

      const { user } = await signInAnonymously(auth);

      await recordLogin();

      setUserId(user.uid);
      console.log("setUserId -> 10 (anonymous)");
      localStorage.setItem("tujud_uid", user.uid);
      localStorage.setItem("anonymous_tujud_uid", user.uid);

      navigate("/app/home", { replace: true });
    } catch (error: any) {
      console.error("Anonymous sign-in failed:", error);
      setError("Failed to start explore mode. Please try again.");
    }
  };

  const handleIntroComplete = () => {
    setIntroCompleted(true);
    setSignUp(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("tunded_intro_seen", "true");
    }
  };
  
  const addOrganization = async (organizationName: string) => {
    console.log("store organization");

    Sentry.setUser({ id: "test", userId: "test" });
    Sentry.logger.info("User added organization", {
      log_source: "sentry_test",
    });

    const organizationId = await OrganizationService.addOrganization(
      organizationName,
      userId
    );
    return organizationId;
  };

  const sendMagicLink = async () => {
    try {
      setSuccessMessage("");
      setError("");

      const email = emailForMagic.trim();
      if (!email) {
        setError("Enter email to receive a sign-in link.");
        return;
      }

      if (loadingStates?.users) {
        setError("Loading user data, please wait...");
        return;
      }

      // DEVELOPMENT BYPASS: Skip magic link for experienceapp.io test emails or dev hostnames
      const isProductionTestEmail =
        window.location.hostname === appConfig.productionHostname &&
        appConfig.testEmails.includes(email);

      const isDevelopmentTestEmail =
        window.location.hostname === appConfig.developmentHostname &&
        appConfig.testEmails.includes(email);

      const isBypassAuthHostname = appConfig.bypassAuthHostnames.includes(
        window.location.hostname
      );

      console.log("isBypassAuthHostname:", isBypassAuthHostname);
      console.log("email: " + email);

      if (
        isProductionTestEmail ||
        isBypassAuthHostname ||
        isDevelopmentTestEmail
      ) {
        setIsLoading(true);

        const result = await handleTestAccountBypass({
          email,
          setUserId,
          setEmail,
          setMoods,
          setCauses,
          refreshData,
          navigate,
        });

        if (!result.success) {
          setError(result.error || "Test account setup error");
        }
        setIsLoading(false);
        return;
      }

      console.log("email2");
      // Production magic link flow
      setIsLoading(true);
      setError("");
      setSuccessMessage("");
      console.log("email3");

      await sendMagicLinkEmail(email);
      setSuccessMessage("");
      setShowCodeInput(true);
      console.log("email4");
    } catch (e: any) {
      console.error("sendMagicLink error:", e);
      setError(`Could not send the link: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailFormBack = () => {
    setShowEmailForm(false);
    setError("");
    setSuccessMessage("");
  };

  const handleCodeVerified = async () => {
    try {
      setIsLoading(true);
      setError("");

      const email = emailForMagic.trim().toLowerCase();

      const { auth } = await import("../../config/firebase");
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error(
          "User not authenticated. Please verify the code again."
        );
      }

      const userUid = firebaseUser.uid;

      const existingAccount = await getAccount().catch(() => null);
      const isExistingUser = !!existingAccount;

      if (afterFirebaseAuthBeforeLoginUser) {
        await afterFirebaseAuthBeforeLoginUser({
          userUid,
          email,
          isExistingUser,
        });
      }

      await loginUser(email, userUid, isExistingUser);
    } catch (e: any) {
      console.error("Code verification error:", e);
      setError(`Login failed: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setSuccessMessage("");
    setError("");
  };

  return {
    error,
    emailForMagic,
    signUp,
    isLoading,
    successMessage,
    showEmailForm,
    showCodeInput,
    selectedOption,
    introCompleted,

    setEmailForMagic,
    handleOptionSelect,
    handleExplore,
    handleIntroComplete,
    addOrganization,
    sendMagicLink,
    handleEmailFormBack,
    handleCodeVerified,
    handleBackToEmail,

    isOnline,
    loadingStates,
  };
}
